import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import { initializeTracking, trackView, trackPageView } from '@/lib/tracking';
import type { Property, User } from '@/types';
import CorretorHeader from '@/components/corretor/CorretorHeader';
import PropertySearch from '@/components/property/PropertySearch';
import PromotionalBanner from '@/components/corretor/PromotionalBanner';
import PropertyCard from '@/components/property/PropertyCard';
import { Link } from 'react-router-dom';

export default function CorretorPage() {
  const { slug } = useParams();
  const [properties, setProperties] = useState<Property[]>([]);
  const [organizedProperties, setOrganizedProperties] = useState<{[key: string]: Property[]}>({});
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [corretor, setCorretor] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<any>(null);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (slug) {
      fetchData();
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'VitrineTurbo - Sua Vitrine Digital';
      // Reset theme to original when leaving broker page
      document.body.classList.remove('broker-theme');
      document.documentElement.style.removeProperty('--broker-primary');
      document.documentElement.style.removeProperty('--broker-primary-foreground');
      document.documentElement.style.removeProperty('--broker-accent');
      document.documentElement.style.removeProperty('--broker-accent-foreground');
    };
  }, [slug]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch broker by slug first
      const { data: corretorData, error: corretorError } = await supabase
        .from('users')
        .select('*')
        .eq('slug', slug)
        .eq('role', 'corretor')
        .eq('is_blocked', false)
        .single();
      
      if (corretorError) {
        console.error('Error fetching broker:', corretorError);
        throw new Error('Corretor não encontrado');
      }
      
      if (!corretorData) {
        setError('Corretor não encontrado');
        return;
      }
      
      setCorretor(corretorData);

      // Set page title with broker's name
      document.title = `${corretorData.name} | VitrineTurbo`;

      // Initialize tracking for this broker
      await initializeTracking(corretorData.id);
      
      // Track page view
      await trackPageView(corretorData.id, {
        page_title: `${corretorData.name} | VitrineTurbo`,
        page_location: window.location.href,
        content_group1: 'imoveis'
      });

      // Load storefront settings
      const { data: storefrontSettings } = await supabase
        .from('user_storefront_settings')
        .select('*')
        .eq('user_id', corretorData.id)
        .maybeSingle();

      // Handle both old flat structure and new nested structure
      let effectiveSettings = {
        showFilters: true,
        showSearch: true,
        showPriceRange: true,
        showStatus: true,
        showCategory: true,
        showCity: true,
        showBedrooms: true,
        showBathrooms: true,
        showSuites: true,
        showParkingSpaces: true,
        showArea: true,
        categoryOrganization: {
          enabled: false,
          categories: []
        }
      };

      if (storefrontSettings?.settings) {
        if (storefrontSettings.settings.filters) {
          // New nested structure
          effectiveSettings = {
            ...effectiveSettings,
            ...storefrontSettings.settings.filters,
            categoryOrganization: storefrontSettings.settings.categoryOrganization || effectiveSettings.categoryOrganization
          };
        } else {
          // Old flat structure - maintain backward compatibility
          effectiveSettings = {
            ...effectiveSettings,
            ...storefrontSettings.settings
          };
        }
      }

      setSettings(effectiveSettings);

      // Set theme based on broker's preference and update the theme context
      if (corretorData.theme) {
        document.documentElement.className = corretorData.theme;
        setTheme(corretorData.theme); // Update theme context
      }
      
      // Apply broker's custom colors safely
      try {
        const root = document.documentElement;
        root.style.setProperty('--broker-primary', corretorData.primary_color || '#0f172a');
        root.style.setProperty('--broker-primary-foreground', corretorData.primary_foreground || '#f8fafc');
        root.style.setProperty('--broker-accent', corretorData.accent_color || '#6366f1');
        root.style.setProperty('--broker-accent-foreground', corretorData.accent_foreground || '#ffffff');
        document.body.classList.add('broker-theme');
      } catch (e) {
        console.error('Error applying custom colors:', e);
      }
      
      // Fetch properties
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', corretorData.id)
        .eq('is_visible_on_storefront', true)
        .order('created_at', { ascending: false });
        
      if (propertiesError) throw propertiesError;
      
      setProperties(propertiesData || []);
      
      // Organize properties by category if enabled
      if (effectiveSettings.categoryOrganization?.enabled) {
        organizePropertiesByCategory(propertiesData || [], effectiveSettings.categoryOrganization);
      } else {
        setFilteredProperties((propertiesData || []).slice(0, 12));
      }
      
    } catch (err: any) {
      console.error('Error in fetchData:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const organizePropertiesByCategory = (propertiesData: Property[], categoryConfig: any) => {
    const organized: {[key: string]: Property[]} = {};
    const uncategorized: Property[] = [];

    // Get enabled categories sorted by order
    const enabledCategories = categoryConfig.categories
      .filter((cat: any) => cat.enabled)
      .sort((a: any, b: any) => a.order - b.order);

    // Initialize organized object with enabled categories
    enabledCategories.forEach((cat: any) => {
      organized[cat.name] = [];
    });

    // Organize properties
    propertiesData.forEach(property => {
      if (property.category && organized[property.category]) {
        const categoryConfig = enabledCategories.find((cat: any) => cat.name === property.category);
        if (categoryConfig && organized[property.category].length < categoryConfig.maxItems) {
          organized[property.category].push(property);
        }
      } else {
        uncategorized.push(property);
      }
    });

    // Add uncategorized properties at the end
    if (uncategorized.length > 0) {
      organized['Outros'] = uncategorized.slice(0, 12);
    }

    setOrganizedProperties(organized);
  };

  const handleSearch = (filters: any) => {
    let filtered = [...properties];

    // Only apply filters if enabled in settings
    if (settings?.showSearch && filters.query) {
      const query = filters.query.toLowerCase();
      filtered = filtered.filter(property =>
        property.title.toLowerCase().includes(query) ||
        property.description.toLowerCase().includes(query)
      );
    }

    // Status filter - only if enabled
    if (settings?.showStatus && filters.status !== 'todos') {
      filtered = filtered.filter(property => property.type === filters.status);
    }

    // Category filter - only if enabled
    if (settings?.showCategory && filters.category !== 'todos') {
      filtered = filtered.filter(property => property.category === filters.category);
    }

    // City filter - only if enabled
    if (settings?.showCity && filters.city !== 'todos') {
      filtered = filtered.filter(property => property.city === filters.city);
    }

    // Bedrooms filter - only if enabled
    if (settings?.showBedrooms && filters.bedrooms !== 'todos') {
      const bedroomsNumber = parseInt(filters.bedrooms);
      filtered = filtered.filter(property =>
        filters.bedrooms === '4' ? property.bedrooms >= 4 : property.bedrooms === bedroomsNumber
      );
    }

    // Bathrooms filter - only if enabled
    if (settings?.showBathrooms && filters.bathrooms !== 'todos') {
      const bathroomsNumber = parseInt(filters.bathrooms);
      filtered = filtered.filter(property =>
        filters.bathrooms === '4' ? property.bathrooms >= 4 : property.bathrooms === bathroomsNumber
      );
    }

    // Suites filter - only if enabled
    if (settings?.showSuites && filters.suites !== 'todos') {
      const suitesNumber = parseInt(filters.suites);
      filtered = filtered.filter(property =>
        filters.suites === '4' ? property.suites >= 4 : property.suites === suitesNumber
      );
    }

    // Parking spaces filter - only if enabled
    if (settings?.showParkingSpaces && filters.parkingSpaces !== 'todos') {
      const parkingNumber = parseInt(filters.parkingSpaces);
      filtered = filtered.filter(property =>
        filters.parkingSpaces === '4' ? property.parking_spaces >= 4 : property.parking_spaces === parkingNumber
      );
    }

    // Price range filter if enabled
    if (settings?.showPriceRange) {
      filtered = filtered.filter(property =>
        property.price >= filters.minPrice && property.price <= filters.maxPrice
      );
    }

    // Area range filter if enabled
    if (settings?.showArea) {
      filtered = filtered.filter(property =>
        property.area >= filters.minArea && property.area <= filters.maxArea
      );
    }

    // Apply pagination based on settings
    setFilteredProperties(filtered.slice(0, 12));
    
    // Reset organized view when searching
    setOrganizedProperties({});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Carregando Vitrine...</p>
      </div>
    );
  }

  if (error || !corretor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Corretor não encontrado</h2>
        <p className="text-muted-foreground mb-6 text-center">
          O corretor que você está procurando não existe ou não está disponível.
        </p>
        <Button asChild>
          <Link to="/">Voltar para a página inicial</Link>
        </Button>
      </div>
    );
  }

  const isOrganizedView = settings?.categoryOrganization?.enabled && Object.keys(organizedProperties).length > 0;

  return (
    <div className="flex-1">
      {/* Hero section */}
      <CorretorHeader corretor={corretor} />

      {/* Search and filters */}
      {settings?.showFilters && (
        <div className="container mx-auto px-5 mb-8">
          <PropertySearch 
            onSearch={handleSearch} 
            properties={properties} 
            settings={settings}
          />
        </div>
      )}

      {/* Promotional Banner */}
      <PromotionalBanner corretor={corretor} />
      
      {/* Properties grid */}
      <div className="container mx-auto px-5 mb-16">
        {isOrganizedView ? (
          // Organized by categories
          <div className="space-y-12">
            {Object.entries(organizedProperties).map(([categoryName, categoryProperties]) => (
              categoryProperties.length > 0 && (
                <div key={categoryName}>
                  <h2 className="text-2xl font-bold mb-6">{categoryName}</h2>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    {categoryProperties.map((property, index) => (
                      <PropertyCard 
                        key={property.id} 
                        property={property} 
                        corretorSlug={slug!} 
                        index={index}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          // Regular grid view
          <>
            {filteredProperties.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Nenhum imóvel encontrado com os filtros selecionados.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {filteredProperties.map((property, index) => (
                  <PropertyCard 
                    key={property.id} 
                    property={property} 
                    corretorSlug={slug!} 
                    index={index}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}